import React from 'react';
import { Check } from 'lucide-react';
import RobbieMark from './RobbieMark';
import SegmentedProgress, { ProgressCounts, ProgressLabels } from './SegmentedProgress';

/**
 * Progress strip — a long job stays visible on the page after its pop-up
 * closes: Robbie working, the job's name, the segmented bar + counts, a
 * forward estimate ("about 12–16 min more" — never elapsed time) and an
 * optional action. `done` turns it green; `attention` amber.
 */
export type ProgressStripTone = 'running' | 'done' | 'attention';

export interface ProgressStripProps {
  title: React.ReactNode;
  counts: ProgressCounts;
  labels?: ProgressLabels;
  tone?: ProgressStripTone;
  /** Forward-looking estimate, e.g. <>about <b>12–16 min</b> more</>. */
  eta?: React.ReactNode;
  /** Right-aligned action(s): "Open", "Retry 2". */
  end?: React.ReactNode;
  className?: string;
}

const TONES: Record<ProgressStripTone, string> = {
  running: 'border-robbie-border bg-ai-chip',
  done: 'border-success-border bg-success-bg',
  attention: 'border-warn-border bg-warn-bg',
};

export const ProgressStrip: React.FC<ProgressStripProps> = ({
  title,
  counts,
  labels,
  tone = 'running',
  eta,
  end,
  className = '',
}) => (
  <div
    role="status"
    aria-live="polite"
    className={`flex min-h-[44px] flex-wrap items-center gap-s5 rounded-card border px-s5 py-s3 text-[12.5px] text-ink-body ${TONES[tone]} ${className}`}
  >
    <span className="inline-flex items-center gap-s3 whitespace-nowrap font-semibold text-ink">
      {tone === 'done' ? (
        <span className="grid h-5 w-5 place-items-center rounded-round bg-success-ink text-white">
          <Check aria-hidden="true" className="h-3 w-3" />
        </span>
      ) : (
        <RobbieMark state={tone === 'running' ? 'working' : 'idle'} size="xs" aria-label="Robbie" />
      )}
      {title}
    </span>
    <SegmentedProgress counts={counts} labels={labels} inline />
    {eta && <span className="whitespace-nowrap text-ink-secondary">{eta}</span>}
    {end && <span className="ml-auto inline-flex items-center gap-s5 whitespace-nowrap">{end}</span>}
  </div>
);

export default ProgressStrip;
